import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BsdfDiffuseSockets} from "#material/nodes/shader/bsdf-diffuse/definition"
import {bsdfDiffuseDefinition} from "#material/nodes/shader/bsdf-diffuse/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeRGBColor, threeValue, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {ThreeReturns} from "#material/sockets/three"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import type {DiffuseBsdfNode} from "#material/cycles/types"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"

@registerMaterialGraphNode
export class BsdfDiffuse extends MaterialNode<BsdfDiffuseSockets> {
    public override get definition() {
        return bsdfDiffuseDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BsdfDiffuseSockets>> => {
        const material = new ThreeColormassMaterial()

        const {color, normal} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            normal: this.parameters.inputs.normal,
        })

        material.colorNode = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? material.colorNode
        material.roughnessNode = threeValue(1) // Always 1 for Lambertian diffuse (Oren-Nayar not supported yet)
        material.metalnessNode = threeValue(0)
        material.specularIntensityNode = threeValue(0)

        material.normalNode =
            normal?.toVec3() ??
            threeConvert(this.parameters.settings.normal, threeVec3, (val) => val.x !== 0 || val.y !== 0 || val.z !== 0) ??
            material.normalNode

        return {bsdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BsdfDiffuseSockets>> => {
        const bsdf: DiffuseBsdfNode = {
            type: "diffuse_bsdf",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                normal: this.parameters.inputs.normal,
                roughness: this.parameters.inputs.roughness,
                surface_mix_weight: undefined,
            }),
            parameters: {
                color: cyclesColor(this.parameters.settings.color),
                roughness: this.parameters.settings.roughness,
                normal: cyclesVec3(this.parameters.settings.normal),
                surface_mix_weight: undefined,
            },
            resources: {},
        }

        return {bsdf: [bsdf, "bsdf"]}
    }
}
